'use client';

import Logo from '@/lib/logo/Logo';
import { LogLevel } from '@logtail/next';
import { useLogger } from '@logtail/next/hooks';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { type ReactElement, useEffect } from 'react';

// Runtime error boundary for the (consent-manger) segment tree.
// 404s are NOT handled here — those go to the dedicated not-found.tsx route
// via Next.js's notFound() mechanism or the [...rest] catch-all. This file
// exclusively covers uncaught runtime exceptions (500-class) so there's one
// visual treatment per failure mode.
export default function ErrorPage({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}): ReactElement {
  const t = useTranslations('ErrorPage');
  const pathname = usePathname();
  const log = useLogger({ source: 'error.tsx' });

  // Once per error, not once per render: the boundary re-renders on any
  // parent/state change while it is shown. `log` is memoized by useLogger
  // (stable for a given path), so the effect re-fires only for a new error.
  useEffect(() => {
    log.logHttpRequest(
      LogLevel.error,
      error.message,
      {
        host: window.location.href,
        path: pathname,
        statusCode: 500,
      },
      {
        error: error.name,
        cause: error.cause,
        stack: error.stack,
        digest: error.digest,
      }
    );
  }, [error, log, pathname]);

  return (
    <main className='min-h-[60vh] flex items-center justify-center px-6'>
      <div className='max-w-xl text-center'>
        <div className='mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-brand-tertiary/10 to-status-info/10 ring-1 ring-brand-tertiary/20'>
          <Logo showText={false} />
        </div>
        <h2 className='text-lexomat-xl font-semibold tracking-tight text-content-primary'>
          {t('titleGeneric')}
        </h2>
        <p className='mt-3 text-lexomat-base text-content-secondary'>
          {t('descGeneric')}
        </p>

        {error.message ? (
          <p className='mt-2 text-sm text-content-muted'>{error.message}</p>
        ) : null}

        <div className='mt-8 flex items-center justify-center gap-3'>
          <button
            type='button'
            onClick={reset}
            // #8795: was text-content-inverse — near-black (#141413) in the
            // default/calm/warm dark palettes against this gradient
            // (2.85-3.41:1, fails AA); text-cta-text resolves to solid
            // #ffffff in all 8 palette/mode combos and clears AA everywhere.
            className='shimmer-button inline-flex items-center justify-center rounded-md [background:var(--gradient-button)] px-5 py-2 text-sm font-medium text-cta-text shadow hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary-color'
          >
            {t('tryAgain')}
          </button>
          <Link
            href='/'
            className='inline-flex items-center justify-center rounded-md border border-line-default px-5 py-2 text-sm font-medium text-content-secondary hover:bg-surface-subtle'
          >
            {t('goHome')}
          </Link>
        </div>

        {error.digest ? (
          <p className='mt-6 text-xs text-content-muted'>
            {t('errorReference')}{' '}
            <code className='font-mono'>{error.digest}</code>
          </p>
        ) : null}
      </div>
    </main>
  );
}
